import React, { useCallback, useEffect, useState } from 'react';
import { IconButton, Tooltip } from '@mui/material';
import IosShareIcon from '@mui/icons-material/IosShare';
import { useParams } from 'react-router-dom';
import cityExplorerService from '../../services/cityExplorerService';
import ExplorerView from './ExplorerView';
import ShareLinksDialog from './ShareLinksDialog';

const FOOTER_OFFSET_PX = 72;

const LocationExplorer = () => {
  const { locationId, categoryId, fileId, resourceId } = useParams();
  const [location, setLocation] = useState(null);
  const [loadingLocation, setLoadingLocation] = useState(true);
  const [locationError, setLocationError] = useState(null);
  const [shareOpen, setShareOpen] = useState(false);
  const [shareResourceIds, setShareResourceIds] = useState(null);

  const openShare = (resourceIds) => {
    setShareResourceIds(resourceIds || null);
    setShareOpen(true);
  };

  const closeShare = () => {
    setShareOpen(false);
    setShareResourceIds(null);
  };

  useEffect(() => {
    let active = true;
    setLoadingLocation(true);
    setLocationError(null);

    (async () => {
      try {
        const data = await cityExplorerService.getLocation(locationId);
        if (active) setLocation(data);
      } catch (err) {
        if (active) setLocationError(err.message || 'Failed to load this location');
      } finally {
        if (active) setLoadingLocation(false);
      }
    })();

    return () => {
      active = false;
    };
  }, [locationId]);

  const fetchMapUrl = useCallback(
    (mapCategoryId, mapFileId, disposition = 'inline') =>
      cityExplorerService.getMapUrl(locationId, mapCategoryId, mapFileId, disposition),
    [locationId]
  );

  const fetchResourceUrl = useCallback(
    (mapResourceId, disposition = 'inline') =>
      cityExplorerService.getResourceUrl(locationId, mapResourceId, disposition),
    [locationId]
  );

  return (
    <>
      <ExplorerView
        location={location}
        loadingLocation={loadingLocation}
        locationError={locationError}
        basePath={`/city-explorer/${locationId}`}
        categoryId={categoryId}
        fileId={fileId}
        resourceId={resourceId}
        fetchMapUrl={fetchMapUrl}
        fetchResourceUrl={fetchResourceUrl}
        fetchSummary={cityExplorerService.getLocationSummary}
        footerOffsetPx={FOOTER_OFFSET_PX}
        showAllLocationsLink
        onShareCalculator={(calculatorId) => openShare([calculatorId || 'opportunity_calculator'])}
        toolbarExtra={
          location ? (
            <Tooltip title="Share maps">
              <IconButton size="small" onClick={() => openShare()} aria-label="Share maps">
                <IosShareIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          ) : null
        }
      />
      <ShareLinksDialog
        open={shareOpen}
        onClose={closeShare}
        location={location}
        initialResourceIds={shareResourceIds}
      />
    </>
  );
};

export default LocationExplorer;
